import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Lock,
  Minus,
  Plus,
  ShieldAlert,
  Terminal,
  X,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import EmberCanvas from "@/components/EmberCanvas";
import {
  ARSENAL_HUB,
  HUB_ATTRIBUTION,
  HUB_FAQS,
  HUB_FEATURES,
  HUB_FIRST_USE,
  HUB_GUIDE_SHOTS,
  HUB_INSTALL_STEPS,
  HUB_SCREENSHOTS,
  HUB_SHARING_STEPS,
  HUB_UPDATE_NOTES,
  HUB_VIDEO,
} from "@/data/arsenalHub";

const CopyButton = ({ text, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button
      onClick={copy}
      data-testid={testid}
      className="group/copy flex shrink-0 items-center gap-1.5 border border-[#282D37] px-2.5 py-1.5 font-mono text-[9px] tracking-[0.2em] text-[#9EA4B0] transition-colors duration-300 hover:border-[#E52E2E]/60 hover:text-[#E52E2E]"
      aria-label={`Copy ${text}`}
    >
      {copied ? <Check size={11} className="text-[#E52E2E]" /> : <Copy size={11} />}
      {copied ? "COPIED" : "COPY"}
    </button>
  );
};

const Hero = () => (
  <header data-testid="hub-hero" className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
    <Reveal>
      <Link
        to="/projects"
        data-testid="hub-back-link"
        className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
      >
        <ArrowLeft size={14} /> ALL PROJECTS
      </Link>
      <div className="mt-10 flex flex-wrap items-center gap-6">
        <img
          src="/arsenalhub/arsenal-hub-logo.png"
          alt="Arsenal Hub emblem"
          className="w-20 sm:w-24"
          data-testid="hub-logo"
        />
        <div>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">
            ARSENAL FORGE · WOW FOREVER
          </p>
          <h1 className="mt-2 font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F4F5F7] sm:text-7xl">
            Arsenal Hub
          </h1>
        </div>
      </div>
      <p className="mt-8 max-w-2xl font-display text-2xl font-semibold uppercase leading-snug tracking-wide text-[#C6CAD2] sm:text-3xl">
        Your guild. Your plans. One in-game hub.
      </p>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#9EA4B0] sm:text-lg">
        Organise guild events and raids, share captured gear, connect main and alt profiles, and keep
        useful quest and auction tools close at hand — all inside WoW Forever.
      </p>
      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#5F6573]">
        Live guild updates between compatible online members. Optional cloud backup, not a
        requirement for in-game sharing.
      </p>
      <div className="mt-6 flex flex-wrap gap-2 font-mono text-[10px] font-bold tracking-[0.25em]">
        <span data-testid="hub-badge-game" className="border border-[#282D37] px-3 py-1.5 text-[#F4F5F7]">WOW FOREVER</span>
        <span data-testid="hub-badge-platform" className="border border-[#282D37] px-3 py-1.5 text-[#F4F5F7]">WINDOWS X64</span>
        <span data-testid="hub-badge-release" className="border border-[#E52E2E]/60 bg-[#E52E2E]/10 px-3 py-1.5 text-[#E52E2E]">
          GUILD RELEASE v{ARSENAL_HUB.version}
        </span>
        <span data-testid="hub-badge-guide" className="border border-[#282D37] px-3 py-1.5 text-[#9EA4B0]">GUIDE INCLUDED</span>
      </div>
      <div className="mt-10 flex flex-wrap gap-4">
        <a
          href="#installation"
          data-testid="hub-cta-install-guide"
          className="group flex items-center gap-3 bg-[#E52E2E] px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_28px_rgba(229,46,46,0.3)] transition-colors duration-300 hover:bg-[#FF3B3B]"
        >
          View installation guide
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
        <Link
          to="/contact"
          data-testid="hub-cta-contact"
          className="flex items-center gap-3 border border-[#3A404E] px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
        >
          Contact Arsenal
        </Link>
      </div>
    </Reveal>
  </header>
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Availability = () => {
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/downloads/arsenal-hub/status`)
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => setStatus({ available: false }));
  }, []);

  const available = Boolean(status && status.available);

  const unlock = async (e) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/downloads/arsenal-hub`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.status === 401) throw new Error("Incorrect password.");
      if (res.status === 429) throw new Error("Too many attempts. Try again in a few minutes.");
      if (res.status === 503) throw new Error("Download is not available yet.");
      if (!res.ok) throw new Error("Download failed. Please try again.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = ARSENAL_HUB.download.friendlyFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setPassword("");
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
  <section data-testid="hub-download-panel" className="mx-auto max-w-[1400px] px-6 pt-14 lg:px-10">
    <Reveal>
      <div className="relative overflow-hidden border border-[#E52E2E]/40 bg-[#0D0F12] p-8 sm:p-10">
        <EmberCanvas density={8} className="pointer-events-none absolute inset-0 h-full w-full opacity-25" />
        <div className="relative">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="font-mono text-[11px] tracking-[0.35em] text-[#E52E2E]">
                {ARSENAL_HUB.releaseLabel.toUpperCase()} · {ARSENAL_HUB.download.distribution.toUpperCase()}
              </p>
              <p className="mt-3 max-w-xl font-display text-xl font-bold uppercase tracking-wide text-[#F4F5F7]">
                {available ? "Guild download is live — password required." : "Guild download access is being prepared."}
              </p>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#9EA4B0]">
                {available
                  ? "This build is restricted to the Arsenal guild. Enter the guild password and the server will verify it before sending the installer — the password is never stored in this page."
                  : "This exact build is a private Arsenal guild package. Downloads stay locked while the distribution review is completed and server-side guild-access authorization is put in place. When access opens, approved guild members will download it here — this page will never host the installer publicly."}
              </p>
            </div>
            {available ? (
              <form onSubmit={unlock} data-testid="hub-download-form" className="w-full shrink-0 lg:w-auto">
                <label htmlFor="hub-download-password" className="font-mono text-[10px] tracking-[0.3em] text-[#9EA4B0]">
                  GUILD PASSWORD
                </label>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                  <input
                    id="hub-download-password"
                    data-testid="hub-download-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="off"
                    placeholder="Enter guild password"
                    className="w-full border border-[#3A404E] bg-[#0A0B0D] px-5 py-4 font-mono text-sm tracking-widest text-[#F4F5F7] placeholder-[#5F6573] focus:border-[#E52E2E] focus:outline-none sm:w-64"
                  />
                  <button
                    type="submit"
                    data-testid="hub-download-submit"
                    disabled={busy || !password}
                    className="flex items-center justify-center gap-3 bg-[#E52E2E] px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_28px_rgba(229,46,46,0.3)] transition-colors duration-300 hover:bg-[#FF3B3B] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Lock size={14} />
                    {busy ? "Verifying…" : "Download for Windows"}
                  </button>
                </div>
                {error && (
                  <p data-testid="hub-download-error" className="mt-3 font-mono text-[11px] tracking-[0.15em] text-[#E52E2E]">
                    {error.toUpperCase()}
                  </p>
                )}
                {done && !error && (
                  <p data-testid="hub-download-success" className="mt-3 font-mono text-[11px] tracking-[0.15em] text-[#9EA4B0]">
                    INSTALLER DOWNLOADED — VERIFY THE CHECKSUM BELOW BEFORE RUNNING IT.
                  </p>
                )}
              </form>
            ) : (
              <span
                data-testid="hub-download-unavailable"
                className="flex shrink-0 items-center gap-3 border border-[#282D37] px-8 py-5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#5F6573]"
              >
                <Lock size={15} /> Download unavailable
              </span>
            )}
          </div>
          <div className="mt-8 grid gap-px border border-[#282D37] bg-[#282D37] sm:grid-cols-2 lg:grid-cols-3">
            {[
              ["FILE", ARSENAL_HUB.download.friendlyFilename],
              ["SIZE", ARSENAL_HUB.download.sizeDisplay],
              ["PLATFORM", ARSENAL_HUB.platform],
              ["ADDON INTERFACE", ARSENAL_HUB.addonInterface],
              ["BUILD DATE", ARSENAL_HUB.buildDate],
              ["SIGNING", "UNSIGNED"],
            ].map(([k, v]) => (
              <div key={k} className="bg-[#0A0B0D] px-5 py-4">
                <p className="font-mono text-[9px] tracking-[0.3em] text-[#5F6573]">{k}</p>
                <p className="mt-1.5 break-all font-mono text-xs font-bold tracking-wider text-[#F4F5F7]">{v}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 border border-[#282D37] bg-[#0A0B0D] px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              <p className="font-mono text-[9px] tracking-[0.3em] text-[#5F6573]">SHA-256</p>
              <CopyButton text={ARSENAL_HUB.download.sha256} testid="hub-copy-sha256" />
            </div>
            <p data-testid="hub-sha256" className="mt-1.5 break-all font-mono text-xs tracking-wider text-[#9EA4B0]">
              {ARSENAL_HUB.download.sha256}
            </p>
          </div>
          <div className="mt-6 flex gap-4 border border-[#E52E2E]/30 bg-[#E52E2E]/5 p-5">
            <ShieldAlert size={18} className="mt-0.5 shrink-0 text-[#E52E2E]" />
            <p className="text-sm leading-relaxed text-[#9EA4B0]">
              <span className="font-bold text-[#F4F5F7]">This release is unsigned.</span> Windows may
              show an unrecognized-app or unknown-publisher warning. Verify the website, release
              version and published checksum before running it. Do not disable antivirus or Windows
              security protections. Contact Arsenal if the file is blocked or reported as a threat.
            </p>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
  );
};

const Tabs = () => {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const close = () => setLightbox(null);
  const step = (dir) =>
    setLightbox((i) => (i + dir + HUB_SCREENSHOTS.length) % HUB_SCREENSHOTS.length);

  return (
    <section data-testid="hub-nav-tabs" className="mx-auto max-w-[1400px] px-6 pt-24 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">THE ACTUAL NAVIGATION</p>
        <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
          Six tabs. Nothing hidden.
        </h2>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#9EA4B0]">
          This is the real top navigation of Arsenal Hub v{ARSENAL_HUB.version} — no unreleased tabs,
          no placeholder features. Pick a tab to see its genuine in-game screen.
        </p>
        <div role="tablist" aria-label="Arsenal Hub tabs" className="mt-10 flex flex-wrap gap-px border border-[#282D37] bg-[#282D37]">
          {ARSENAL_HUB.tabs.map((tab, i) => (
            <button
              key={tab}
              role="tab"
              aria-selected={active === i}
              data-testid={`hub-tab-${tab.toLowerCase()}`}
              onClick={() => setActive(i)}
              className={`flex-1 whitespace-nowrap px-6 py-4 text-center font-mono text-[11px] font-bold tracking-[0.25em] transition-colors duration-300 ${
                active === i
                  ? "bg-[#E52E2E] text-white"
                  : "bg-[#121418] text-[#9EA4B0] hover:text-[#F4F5F7]"
              }`}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="mt-6 border border-[#282D37] bg-[#0A0B0D]">
          <button
            data-testid="hub-tab-shot"
            onClick={() => setLightbox(active)}
            aria-label={`Enlarge screenshot: ${HUB_SCREENSHOTS[active].caption}`}
            className="group relative block w-full cursor-zoom-in overflow-hidden"
          >
            <motion.img
              key={HUB_SCREENSHOTS[active].src}
              src={HUB_SCREENSHOTS[active].src}
              alt={HUB_SCREENSHOTS[active].caption}
              initial={{ opacity: 0, scale: 0.985 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[70vh] w-full object-contain"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0B0D]/90 to-transparent p-5 text-left">
              <p data-testid="hub-tab-shot-caption" className="font-mono text-[10px] tracking-[0.3em] text-[#9EA4B0]">
                {HUB_SCREENSHOTS[active].caption.toUpperCase()}
              </p>
            </div>
          </button>
        </div>
      </Reveal>
      <ShotLightbox shots={HUB_SCREENSHOTS} index={lightbox} onClose={close} onStep={step} prefix="hub-tab" />
    </section>
  );
};

const Demo = () => (
  <section data-testid="hub-demo" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
    <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 py-24 lg:grid-cols-[1fr_1.6fr] lg:gap-16 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">DEMO // IN-GAME FOOTAGE</p>
        <h2 className="mt-4 font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
          Watch it work
        </h2>
        <p className="mt-6 text-sm leading-relaxed text-[#9EA4B0] sm:text-base">
          Real in-game footage of the released v{ARSENAL_HUB.version} build inside WoW Forever —
          the actual interface guildmates use, captured live.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="border border-[#282D37] bg-[#0A0B0D]">
          <div className="flex items-center justify-between border-b border-[#282D37] px-4 py-2.5">
            <div className="flex items-center gap-2.5">
              <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
              <span className="font-mono text-[10px] tracking-[0.25em] text-[#9EA4B0]">
                REC · ARSENAL_HUB_V{ARSENAL_HUB.version}
              </span>
            </div>
            <span className="hidden font-mono text-[10px] tracking-[0.2em] text-[#5F6573] sm:block">IN-GAME CAPTURE</span>
          </div>
          <video
            data-testid="hub-demo-video"
            src={HUB_VIDEO.src}
            poster={HUB_VIDEO.poster}
            className="aspect-[992/718] w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            controls
            preload="metadata"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

const ShotLightbox = ({ shots, index, onClose, onStep, prefix }) => {
  useEffect(() => {
    if (index === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onStep(-1);
      if (e.key === "ArrowRight") onStep(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, onClose, onStep]);

  return (
    <AnimatePresence>
      {index !== null && (
        <motion.div
          data-testid={`${prefix}-lightbox`}
          role="dialog"
          aria-modal="true"
          aria-label="Screenshot viewer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0A0B0D]/95 p-6 backdrop-blur-md"
        >
          <button
            data-testid={`${prefix}-lightbox-close`}
            onClick={onClose}
            aria-label="Close screenshot viewer"
            className="absolute right-6 top-6 text-[#9EA4B0] transition-colors hover:text-[#E52E2E]"
          >
            <X size={28} />
          </button>
          <button
            data-testid={`${prefix}-lightbox-prev`}
            onClick={(e) => { e.stopPropagation(); onStep(-1); }}
            aria-label="Previous screenshot"
            className="absolute left-4 top-1/2 -translate-y-1/2 border border-[#282D37] bg-[#0A0B0D]/70 p-3 text-[#9EA4B0] transition-colors hover:border-[#E52E2E]/60 hover:text-[#E52E2E] sm:left-8"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            data-testid={`${prefix}-lightbox-next`}
            onClick={(e) => { e.stopPropagation(); onStep(1); }}
            aria-label="Next screenshot"
            className="absolute right-4 top-1/2 -translate-y-1/2 border border-[#282D37] bg-[#0A0B0D]/70 p-3 text-[#9EA4B0] transition-colors hover:border-[#E52E2E]/60 hover:text-[#E52E2E] sm:right-8"
          >
            <ChevronRight size={22} />
          </button>
          <motion.figure
            key={index}
            initial={{ scale: 0.95, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-6xl"
          >
            <img
              src={shots[index].src}
              alt={shots[index].caption}
              className="max-h-[78vh] w-full border border-[#282D37] object-contain"
            />
            <figcaption
              data-testid={`${prefix}-lightbox-caption`}
              className="mt-4 text-center font-mono text-xs leading-relaxed tracking-[0.2em] text-[#9EA4B0]"
            >
              {shots[index].caption.toUpperCase()} · {index + 1}/{shots.length}
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Screenshots = () => {
  const [lightbox, setLightbox] = useState(null);
  const close = () => setLightbox(null);
  const step = (dir) =>
    setLightbox((i) => (i + dir + HUB_SCREENSHOTS.length) % HUB_SCREENSHOTS.length);

  return (
    <section data-testid="hub-screenshots" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
      <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">SCREENSHOTS // IN-GAME v{ARSENAL_HUB.version}</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            Inside the Hub
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#9EA4B0]">
            Genuine captures of the released build — the real WoW metal-and-bronze interface,
            exactly as guildmates see it.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {HUB_SCREENSHOTS.map((s, i) => (
            <Reveal key={s.src} delay={0.06 * i}>
              <button
                data-testid={`hub-screenshot-${i}`}
                onClick={() => setLightbox(i)}
                aria-label={`Enlarge screenshot: ${s.caption}`}
                className="group relative block w-full cursor-zoom-in overflow-hidden border border-[#282D37]"
              >
                <img
                  src={s.src}
                  alt={s.caption}
                  loading="lazy"
                  className="aspect-[989/719] w-full object-cover object-top opacity-90 transition-[transform,opacity] duration-700 group-hover:scale-[1.03] group-hover:opacity-100"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0B0D]/90 to-transparent p-5 text-left">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-[#9EA4B0]">{s.caption.split(" — ")[0].toUpperCase()}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <ShotLightbox shots={HUB_SCREENSHOTS} index={lightbox} onClose={close} onStep={step} prefix="hub" />
    </section>
  );
};

const GuideShowcase = () => {
  const [lightbox, setLightbox] = useState(null);
  const close = () => setLightbox(null);
  const step = (dir) =>
    setLightbox((i) => (i + dir + HUB_GUIDE_SHOTS.length) % HUB_GUIDE_SHOTS.length);

  return (
    <section data-testid="hub-guide-showcase" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">INCLUDED IN THE BUNDLE · GUIDE v0.4.16</p>
        <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
          Arsenal Guide rides along
        </h2>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#9EA4B0] sm:text-base">
          Quest guidance, chapter notifications, a larger direction arrow, distance and direction
          information, and a compact, resizable tracker styled to match the Hub. Quest and step
          information is shown when available — and your preferred tracker size and position are
          kept between sessions.
        </p>
      </Reveal>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {HUB_GUIDE_SHOTS.map((s, i) => (
          <Reveal key={s.src} delay={0.06 * i}>
            <button
              data-testid={`hub-guide-shot-${i}`}
              onClick={() => setLightbox(i)}
              aria-label={`Enlarge screenshot: ${s.caption}`}
              className="group relative block w-full cursor-zoom-in overflow-hidden border border-[#282D37] bg-[#0A0B0D]"
            >
              <div className="flex h-64 items-center justify-center sm:h-72">
                <img
                  src={s.src}
                  alt={s.caption}
                  loading="lazy"
                  className="max-h-full w-full object-contain opacity-90 transition-[transform,opacity] duration-700 group-hover:scale-[1.03] group-hover:opacity-100"
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A0B0D]/90 to-transparent p-4 text-left">
                <p className="font-mono text-[10px] tracking-[0.3em] text-[#9EA4B0]">{s.label}</p>
              </div>
            </button>
          </Reveal>
        ))}
      </div>

      <ShotLightbox shots={HUB_GUIDE_SHOTS} index={lightbox} onClose={close} onStep={step} prefix="hub-guide" />
    </section>
  );
};

const Overview = () => (
  <section data-testid="hub-overview" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
    <Reveal>
      <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">OVERVIEW</p>
      <h2 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
        Spend less time chasing updates and more time playing together.
      </h2>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#9EA4B0]">
        Arsenal Hub gives your guild one place to plan events, organise raids and view shared member
        information. Supported changes are exchanged directly between compatible addons while members
        are online. The included Arsenal Guide adds quest guidance and a resizable direction tracker,
        while Auctions helps you research prices and organise personal shopping lists.
      </p>
      <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#9EA4B0]">
        Arsenal Guide is included in the Hub bundle — quest guidance, chapter notifications and a
        resizable direction tracker, styled to match the Hub.
      </p>
    </Reveal>
  </section>
);

const Features = () => (
  <section data-testid="hub-features" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
    <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">FEATURES</p>
        <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
          One hub, whole guild
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {HUB_FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={0.06 * i}>
            <div
              data-testid={`hub-feature-${i}`}
              className="group steel-panel h-full p-7 transition-[border-color] duration-500 hover:border-[#E52E2E]/50"
            >
              <p className="font-mono text-sm tracking-[0.3em] text-[#5F6573] transition-colors duration-500 group-hover:text-[#E52E2E]">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-[#F4F5F7]">
                {f.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#9EA4B0]">{f.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Sharing = () => (
  <section data-testid="hub-sharing" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
    <Reveal>
      <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">HOW LIVE SHARING WORKS</p>
      <h2 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
        Create a plan → share it in-game → guildmates receive the update.
      </h2>
    </Reveal>
    <div className="mt-12 grid gap-6 lg:grid-cols-3">
      {HUB_SHARING_STEPS.map((s, i) => (
        <Reveal key={s.step} delay={0.08 * i}>
          <div data-testid={`hub-sharing-step-${i}`} className="h-full border border-[#282D37] bg-[#121418] p-7">
            <p className="font-mono text-sm font-bold tracking-[0.3em] text-[#E52E2E]">{s.step}</p>
            <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-[#F4F5F7]">{s.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#9EA4B0]">{s.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal delay={0.2}>
      <p className="mt-8 max-w-3xl text-sm leading-relaxed text-[#5F6573]">
        Delivery depends on compatible online peers — it is not an always-on server-hosted feed, and
        it is not instantaneous under every condition. Website sign-in is only used to authorize
        downloading this private package; it is not a requirement for native in-game sharing.
      </p>
    </Reveal>
  </section>
);

const Installation = () => (
  <section id="installation" data-testid="hub-install" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
    <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">INSTALLATION</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            One installer. No build tools.
          </h2>
          <ol className="mt-10 space-y-4">
            {HUB_INSTALL_STEPS.map((step, i) => (
              <li key={i} data-testid={`hub-install-step-${i + 1}`} className="flex gap-4 border border-[#282D37] bg-[#121418] p-5">
                <span className="font-mono text-sm font-bold text-[#E52E2E]">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <p className="text-sm leading-relaxed text-[#9EA4B0]">{step.text}</p>
                  {step.copy && (
                    <div className="mt-3 flex items-center justify-between gap-3 border border-[#282D37] bg-[#0A0B0D] px-4 py-2.5">
                      <code className="break-all font-mono text-xs text-[#F4F5F7]">{step.copy}</code>
                      <CopyButton text={step.copy} testid={`hub-copy-install-${i + 1}`} />
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-8 border border-[#282D37] bg-[#121418] p-6">
            <p className="font-mono text-[10px] font-bold tracking-[0.3em] text-[#E52E2E]">UPDATING</p>
            <ul className="mt-4 space-y-3">
              {HUB_UPDATE_NOTES.map((n, i) => (
                <li key={i} data-testid={`hub-update-note-${i}`} className="text-sm leading-relaxed text-[#9EA4B0]">
                  {n}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">FIRST USE</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            Slash &amp; go
          </h2>
          <div className="mt-10 space-y-px">
            {HUB_FIRST_USE.map((c, i) => (
              <div
                key={c.label}
                data-testid={`hub-firstuse-${i}`}
                className="flex items-center justify-between gap-4 border border-[#282D37] bg-[#121418] px-5 py-4 transition-[border-color] duration-300 hover:border-[#E52E2E]/40"
              >
                <div className="flex items-start gap-4">
                  <Terminal size={14} className="mt-0.5 shrink-0 text-[#E52E2E]" />
                  <div>
                    <p className="font-mono text-xs font-bold tracking-[0.15em] text-[#F4F5F7]">{c.label.toUpperCase()}</p>
                    <p className="mt-1 text-xs leading-relaxed text-[#9EA4B0]">{c.detail}</p>
                  </div>
                </div>
                {c.copy && <CopyButton text={c.copy} testid={`hub-copy-firstuse-${i}`} />}
              </div>
            ))}
          </div>
          <p className="mt-6 font-mono text-[11px] leading-relaxed tracking-[0.15em] text-[#5F6573]">
            CLOSING WOW ONCE TO INSTALL REPLACEMENT FILES IS NORMAL — DAILY RAID AND EVENT UPDATES
            NEVER REQUIRE A LOGOUT.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="hub-faq" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">FAQ</p>
        <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
          Asked in guild chat
        </h2>
      </Reveal>
      <div className="mt-12 max-w-3xl space-y-px">
        {HUB_FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <Reveal key={f.q} delay={0.04 * i}>
              <div className={`border bg-[#121418] transition-[border-color] duration-300 ${isOpen ? "border-[#E52E2E]/50" : "border-[#282D37]"}`}>
                <button
                  data-testid={`hub-faq-q-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-display text-base font-bold uppercase tracking-wide text-[#F4F5F7] sm:text-lg">
                    {f.q}
                  </span>
                  {isOpen ? <Minus size={16} className="shrink-0 text-[#E52E2E]" /> : <Plus size={16} className="shrink-0 text-[#5F6573]" />}
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p data-testid={`hub-faq-a-${i}`} className="px-6 pb-6 text-sm leading-relaxed text-[#9EA4B0]">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const ReleaseRecord = () => (
  <section data-testid="hub-release-record" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
    <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10">
      <div className="grid gap-10 lg:grid-cols-2">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">RELEASE RECORD</p>
          <div className="mt-6 space-y-px">
            {ARSENAL_HUB.components.map((c) => (
              <div
                key={c.name}
                data-testid={`hub-component-${c.name.split(" ")[0].toLowerCase()}`}
                className="flex items-center justify-between gap-4 border border-[#282D37] bg-[#121418] px-5 py-4"
              >
                <span className="text-sm text-[#9EA4B0]">{c.name}</span>
                <span className="font-mono text-xs font-bold tracking-[0.15em] text-[#F4F5F7]">v{c.version}</span>
              </div>
            ))}
          </div>
          <p className="mt-5 font-mono text-[11px] tracking-[0.2em] text-[#5F6573]">
            PACKAGE BUILD {ARSENAL_HUB.buildDate} · {ARSENAL_HUB.game.toUpperCase()} · INTERFACE{" "}
            {ARSENAL_HUB.addonInterface} · {ARSENAL_HUB.platform.toUpperCase()}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">CREDITS &amp; SUPPORT</p>
          <p className="mt-5 text-sm leading-relaxed text-[#9EA4B0]">{HUB_ATTRIBUTION}</p>
          <p className="mt-5 text-sm leading-relaxed text-[#9EA4B0]">
            Questions, guild access requests, blocked downloads and SmartScreen reports: join the
            Arsenal Discord at{" "}
            <a
              href={ARSENAL_HUB.support.discord}
              target="_blank"
              rel="noreferrer"
              data-testid="hub-support-discord"
              className="font-bold text-[#E52E2E] transition-colors duration-300 hover:text-[#FF3B3B]"
            >
              {ARSENAL_HUB.support.discordDisplay}
            </a>
            , or send a transmission via{" "}
            <Link
              to="/contact"
              data-testid="hub-support-link"
              className="font-bold text-[#E52E2E] transition-colors duration-300 hover:text-[#FF3B3B]"
            >
              Contact Arsenal
            </Link>
            .
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const ArsenalHub = () => {
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Arsenal Hub",
      applicationCategory: "GameApplication",
      operatingSystem: "Windows x64",
      softwareVersion: ARSENAL_HUB.version,
      author: { "@type": "Organization", name: "Arsenal Forge" },
      description:
        "Plan guild events and raids, share captured gear, follow quests and research auction prices with Arsenal Hub for WoW Forever.",
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "hub-jsonld";
    el.textContent = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => document.getElementById("hub-jsonld")?.remove();
  }, []);

  return (
    <div className="relative pt-32">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
        style={{
          background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
        }}
      />
      <Hero />
      <Availability />
      <Tabs />
      <Demo />
      <Screenshots />
      <GuideShowcase />
      <Overview />
      <Features />
      <Sharing />
      <Installation />
      <Faq />
      <ReleaseRecord />
    </div>
  );
};

export default ArsenalHub;
